import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/** Page header: 28px title + optional subtitle + right-aligned actions. */
export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}

export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ className, title, subtitle, actions, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('mb-6 flex flex-wrap items-start justify-between gap-4', className)}
      {...props}
    >
      <div className="min-w-0">
        <h1 className="text-so-28 font-bold text-sober-text">{title}</h1>
        {subtitle && <div className="mt-1 text-so-14 text-sober-secondary">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
    </div>
  )
);
PageHeader.displayName = 'PageHeader';

const contentGridVariants = cva('grid gap-6', {
  variants: {
    cols: {
      '1': 'grid-cols-1',
      '2': 'grid-cols-1 md:grid-cols-2',
      '3': 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
      '4': 'grid-cols-2 xl:grid-cols-4',
      sidebar: 'grid-cols-1 items-start lg:grid-cols-[2fr_1fr]',
      main: 'grid-cols-1 items-start lg:grid-cols-[1.6fr_1fr]',
    },
  },
  defaultVariants: { cols: '1' },
});

export interface ContentGridProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof contentGridVariants> {}

export const ContentGrid = React.forwardRef<HTMLDivElement, ContentGridProps>(
  ({ className, cols, ...props }, ref) => (
    <div ref={ref} className={cn(contentGridVariants({ cols }), className)} {...props} />
  )
);
ContentGrid.displayName = 'ContentGrid';
